import { ContextMenu, ContextMenuItem } from "@/components/ui";
import type { FirmFile } from "@/lib/types";
import {
  getFirmFileDisplayName,
  getFirmFileExtension,
} from "@/lib/utils/firmFile";
import { Download, Folder, Pencil } from "lucide-react";
import type { ReactNode } from "react";

type FirmFileContextMenuProps = {
  canCreate: boolean;
  canDelete: boolean;
  canDocumentEdit: boolean;
  canUpdate: boolean;
  items: FirmFile[];
  onCreateSubfolder: (folder: FirmFile) => void;
  onDelete: (items: FirmFile[]) => void;
  onDownload: (items: FirmFile[]) => void;
  onEdit: (item: FirmFile) => void;
  onEditDocument: (file: FirmFile) => void;
  trigger: ReactNode;
};

export function FirmFileContextMenu({
  canCreate,
  canDelete,
  canDocumentEdit,
  canUpdate,
  items,
  onCreateSubfolder,
  onDelete,
  onDownload,
  onEdit,
  onEditDocument,
  trigger,
}: FirmFileContextMenuProps) {
  const item = items[0];
  const isBulkAction = items.length > 1;
  const downloadableItems = items.filter(
    (candidate) => !candidate.isFolder && Boolean(candidate.presignedUrlKey),
  );
  const extension = getFirmFileExtension(item).toLowerCase();
  const canEditDocument =
    canUpdate &&
    canDocumentEdit &&
    !item.isFolder &&
    (extension === "docx" || extension === "pdf");

  return (
    <ContextMenu
      entityName={item.isFolder ? "folder" : "file"}
      trigger={trigger}
      open={
        canUpdate && !isBulkAction
          ? {
              onSelect: () => {
                onEdit(item);
              },
            }
          : false
      }
      copy={
        !isBulkAction
          ? {
              label: `Copy ${item.isFolder ? "folder" : "file"} name`,
              successLabel: item.isFolder ? "Folder name" : "File name",
              value: getFirmFileDisplayName(item),
            }
          : false
      }
      delete={
        canDelete
          ? {
              label: isBulkAction ? `Delete ${items.length} files` : undefined,
              onSelect: () => {
                onDelete(items);
              },
            }
          : false
      }
      contentClassName="min-w-[220px]"
    >
      {downloadableItems.length > 0 ? (
        <ContextMenuItem
          onSelect={() => {
            onDownload(downloadableItems);
          }}
        >
          <Download />
          {isBulkAction
            ? `Download ${downloadableItems.length} files`
            : "Download file"}
        </ContextMenuItem>
      ) : null}
      {item.isFolder && canCreate && !isBulkAction ? (
        <ContextMenuItem
          onSelect={() => {
            onCreateSubfolder(item);
          }}
        >
          <Folder className="h-4 w-4" />
          New subfolder
        </ContextMenuItem>
      ) : null}
      {canEditDocument && !isBulkAction ? (
        <ContextMenuItem
          onSelect={() => {
            onEditDocument(item);
          }}
        >
          <Pencil className="h-4 w-4" />
          Edit document
        </ContextMenuItem>
      ) : null}
    </ContextMenu>
  );
}
